import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, Loader2, X } from "lucide-react";
import api from "@/lib/api";

const empty = { title: "", subtitle: "", description: "", product_ids: [], price: 99, image_url: "", status: "active" };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminBundles() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([api.get("/admin/bundles"), api.get("/admin/products")]);
      setRows(b.data); setProducts(p.data);
    } catch { toast.error("Failed to load"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (b) => {
    if (!confirm(`Delete bundle "${b.title}"?`)) return;
    await api.delete(`/admin/bundles/${b.id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-bundles">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Admin · Catalog</p>
          <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Bundles</h1>
          <p className="mt-2 text-sm text-emerald-100/60">{rows.length} bundle{rows.length === 1 ? "" : "s"}. Package products at a discounted set price.</p>
        </div>
        <button onClick={() => setEditing({ ...empty })} data-testid="bundle-new" className="btn-edge text-sm"><Plus size={14} /> New bundle</button>
      </div>

      <div className="card-edge overflow-hidden">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-emerald-500/10 text-xs uppercase tracking-widest text-emerald-100/50">
            <tr><th className="px-5 py-3">Title</th><th className="px-5 py-3">Items</th><th className="px-5 py-3">Sum</th><th className="px-5 py-3">Price</th><th className="px-5 py-3">Saves</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-emerald-100/50">No bundles yet.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} className="border-b border-emerald-500/8 last:border-0" data-testid={`bundle-row-${b.id}`}>
                <td className="px-5 py-3.5 text-white">{b.title}<p className="font-mono-edge text-[10px] text-emerald-100/40">{b.slug}</p></td>
                <td className="px-5 py-3.5 text-emerald-100/70">{b.members?.length ?? b.product_ids?.length ?? 0}</td>
                <td className="px-5 py-3.5 text-emerald-100/60">{money(b.sum_price)}</td>
                <td className="px-5 py-3.5 text-lime-300">{money(b.price)}</td>
                <td className="px-5 py-3.5 text-emerald-300">{money(b.savings)}</td>
                <td className="px-5 py-3.5"><span className={`chip ${b.status === "active" ? "chip-emerald" : "chip-neutral"}`}>{b.status}</span></td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing({ ...b, product_ids: b.product_ids || [] })} data-testid={`bundle-edit-${b.id}`} className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-lime-400/50"><Edit3 size={14} /></button>
                    <button onClick={() => remove(b)} data-testid={`bundle-delete-${b.id}`} className="rounded-lg border border-emerald-500/15 p-1.5 text-emerald-100/80 hover:border-red-500/40 hover:text-red-300"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <BundleModal item={editing} products={products} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BundleModal({ item, products, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const isNew = !item.id;
  const toggle = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  const sum = products.filter((p) => form.product_ids.includes(p.id)).reduce((s, p) => s + Number(p.price), 0);

  const save = async (e) => {
    e.preventDefault();
    if (form.product_ids.length < 2) { toast.error("Pick at least 2 products"); return; }
    setSaving(true);
    try {
      const payload = { title: form.title, subtitle: form.subtitle || "", description: form.description || "", product_ids: form.product_ids, price: Number(form.price) || 0, image_url: form.image_url || null, status: form.status };
      if (isNew) await api.post("/admin/bundles", payload); else await api.put(`/admin/bundles/${item.id}`, payload);
      toast.success("Bundle saved"); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="card-moss my-8 flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-emerald-500/12 p-5">
          <div><p className="overline">{isNew ? "New" : "Edit"} bundle</p><h3 className="mt-1 font-editorial text-xl text-white">{form.title || "Add bundle"}</h3></div>
          <button onClick={onClose} className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={save} className="grid gap-4 overflow-y-auto p-5">
          <F label="Title" v={form.title} on={(v) => setForm({ ...form, title: v })} testId="bundle-title" />
          <F label="Subtitle" v={form.subtitle} on={(v) => setForm({ ...form, subtitle: v })} />
          <F label="Description" v={form.description} on={(v) => setForm({ ...form, description: v })} textarea />
          <F label="Image URL" v={form.image_url} on={(v) => setForm({ ...form, image_url: v })} />
          <div>
            <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Products in bundle</label>
            <div className="grid max-h-52 gap-1 overflow-y-auto">
              {products.map((p) => (
                <label key={p.id} className="flex items-center justify-between gap-2 rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 px-3 py-2 text-sm text-white">
                  <span className="flex items-center gap-2"><input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`bundle-product-${p.id}`} /> {p.title}</span>
                  <span className="text-lime-300">{money(p.price)}</span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-emerald-100/50">Sum of selected: {money(sum)} · buyer saves {money(Math.max(0, sum - Number(form.price || 0)))}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Bundle price" type="number" v={form.price} on={(v) => setForm({ ...form, price: v })} testId="bundle-price" />
            <div>
              <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2.5 text-sm text-white">
                <option value="active">active</option><option value="draft">draft</option>
              </select>
            </div>
          </div>
          <button disabled={saving} data-testid="bundle-save" className="btn-edge justify-center">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        </form>
      </div>
    </div>
  );
}

function F({ label, v, on, textarea, type = "text", testId }) {
  return (
    <div>
      <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">{label}</label>
      {textarea ? (
        <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={3} data-testid={testId} className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
      ) : (
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testId} className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none" />
      )}
    </div>
  );
}
